// hrpayroll/src/pages/Profile/SecurityTab.tsx
import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { updatePassword } from "../../api/password";

type Field = "current" | "next" | "confirm";

export default function SecurityTab() {
  const [current, setCurrent] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [shown, setShown] = useState<Record<Field, boolean>>({
    current: false,
    next: false,
    confirm: false,
  });
  const [busy, setBusy] = useState(false);

  const toggle = (field: Field) =>
    setShown((prev) => ({ ...prev, [field]: !prev[field] }));

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!current) return toast.error("Enter your current password");
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== confirm) return toast.error("Passwords do not match");
    if (pw === current) return toast.error("New password must be different from the current one");
    setBusy(true);
    try {
      await updatePassword(current, pw);
      toast.success("Password updated");
      // The password just saved IS the current one now, so it stays in the
      // Current field (masked) until the next change instead of blanking out.
      setCurrent(pw); setPw(""); setConfirm("");
      setShown({ current: false, next: false, confirm: false });
    } catch (err: any) {
      toast.error(err?.message || "Could not update password");
    } finally { setBusy(false); }
  };

  const field = (
    id: string,
    label: string,
    key: Field,
    value: string,
    onChange: (v: string) => void,
    autoComplete: string,
  ) => (
    <div>
      <Label htmlFor={id} className="text-xs text-gray-500">{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={shown[key] ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          className="pr-10"
        />
        <button
          type="button"
          onClick={() => toggle(key)}
          aria-label={`${shown[key] ? "Hide" : "Show"} ${label.toLowerCase()}`}
          className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-gray-400 hover:text-gray-700"
        >
          {shown[key] ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="py-4 max-w-md">
      <Card>
        <CardHeader><CardTitle className="text-base">Change password</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <p className="text-sm text-gray-500">
              Enter your current password, then choose a new one of at least 6 characters.
            </p>
            {field("current-password", "Current password", "current", current, setCurrent, "current-password")}
            {field("new-password", "New password", "next", pw, setPw, "new-password")}
            {field("confirm-password", "Confirm new password", "confirm", confirm, setConfirm, "new-password")}
            <Button type="submit" disabled={busy || !current || !pw || !confirm}>
              {busy ? "Updating…" : "Update password"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
